import clsx from "clsx";
import React from "react";
import { Icon } from "../icon/Icon";
import {
  chipCloseButton,
  chipContent,
  chipWrapper,
  clickableChip,
  closableChip,
  interactiveChip
} from "./Chip.css";

export interface ChipProps {
  /**
   *  Content to display inside the chip
   */
  children: React.ReactNode;
  /**
   * Optional additional class names
   */
  className?: string;
  /**
   * Whether the chip is disabled
   */
  disabled?: boolean;
  /**
   *  Optional click handler for the chip
   */
  onClick?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  /**
   * Optional close handler, shows a close icon if provided
   */
  onClose?: () => void;
  /**
   * The chip sits inside another button (a select trigger), so its close icon
   * can't be a button of its own and is reached by mouse only
   */
  insideButton?: boolean;
}

export function Chip({
  children,
  className = "",
  disabled = false,
  onClick,
  onClose,
  insideButton = false
}: ChipProps) {
  const isClickable = !!onClick && !disabled && !onClose;

  return (
    <div
      // Only a clickable chip is a button; a closable one holds its own.
      aria-disabled={onClick ? disabled : undefined}
      data-disabled={disabled || undefined}
      role={onClick ? "button" : undefined}
      className={clsx(
        chipWrapper,
        isClickable && clickableChip,
        (onClick || onClose) && interactiveChip,
        onClose && closableChip,
        className
      )}
      onClick={disabled ? undefined : onClick}
    >
      <span className={chipContent}>{children}</span>

      {onClose && insideButton && (
        <div
          role="button"
          aria-disabled={disabled}
          className={chipCloseButton}
          aria-label="Close chip"
          onClick={(event) => {
            if (disabled) return;
            event.stopPropagation();
            onClose();
          }}
        >
          <Icon name="close" />
        </div>
      )}
      {onClose && !insideButton && (
        <button
          type="button"
          disabled={disabled}
          className={chipCloseButton}
          aria-label="Close chip"
          onClick={(event) => {
            event.stopPropagation();
            onClose();
          }}
        >
          <Icon name="close" />
        </button>
      )}
    </div>
  );
}
